import type { Dispatch, FC, ReactNode, SetStateAction } from 'react';
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  createImageCacheHelper,
  preloadImage,
  preloadRemainingImages,
} from '../utils/imageCacheHelper';
import {
  getImageAssets,
  mapCarIdentifier,
  type MappedCar,
  type ImageAsset,
} from '../utils/mapCarline';
import { updateCarline } from '../utils/updateCarline';
import { useFeatureAppEnv } from './FeatureAppEnvContext';

export type ViewMode = 'slides' | '3dStream';

export interface FAStateContextValue {
  readonly imageIndexState: [number, Dispatch<SetStateAction<number>>];
  readonly colorState: [string, Dispatch<SetStateAction<string>>];
  readonly rimState: [string, Dispatch<SetStateAction<string>>];
  readonly carline: MappedCar;
  readonly images: ImageAsset[];
  readonly loading: boolean;
  readonly viewMode: ViewMode;
  readonly setViewMode: React.Dispatch<React.SetStateAction<ViewMode>>;
  readonly idString: string;
  readonly aveString: string;
}

export const FAStateContext = createContext<FAStateContextValue>(
  null as unknown as FAStateContextValue,
);

export interface FAStateContextProviderProps {
  readonly children?: ReactNode;
  readonly carline: MappedCar;
}

export const FAStateContextProvider: FC<FAStateContextProviderProps> = ({ children, carline }) => {
  const {
    'onegraph-service': oneGraphService,
    's2:logger': logger,
    'gfa:locale-service': locale,
  } = useFeatureAppEnv().featureServices;
  const carlineIdentifier = useRef(carline.id);
  const imageIndexState = useState(0);
  const colorState = useState(carline.exteriorColors.find((color) => color.selected)?.pr3 ?? '');
  const rimState = useState(carline.rims.find((rim) => rim.selected)?.pr7 ?? '');
  const [color] = colorState;
  const [rim] = rimState;
  const [images, setImages] = useState(carline.imageAssets);
  const [loading, setLoading] = useState(false);
  const { getCacheItem, storeCacheItem } = useMemo(
    () => createImageCacheHelper(carline.id),
    [carline],
  );
  const [viewMode, setViewMode] = useState<ViewMode>('slides');
  const [imageIndex] = imageIndexState;
  const [idString, setIdString] = useState(carline.idString);
  const [aveString, setAveString] = useState(carline.aveString ?? '');

  useEffect(() => {
    storeCacheItem(
      { color, rim },
      {
        images: carline.imageAssets,
        mappedCarIdentifier: carline.id,
        idString,
        aveString,
      },
    );
  }, []);

  useEffect(() => {
    let mounted = true;
    const hasColorChanged = carlineIdentifier.current.exteriorColor !== color;
    const hasRimChanged = carlineIdentifier.current.equipmentOptions?.indexOf(rim) === -1;

    if (hasColorChanged || hasRimChanged) {
      const cacheId = { color, rim };
      const cacheHit = getCacheItem(cacheId);
      if (cacheHit) {
        carlineIdentifier.current = cacheHit.mappedCarIdentifier;
        setImages(cacheHit.images);
        setIdString(cacheHit.idString);
        setAveString(cacheHit.aveString);
      } else {
        setLoading(true);
        const changedFeature = hasColorChanged ? color : rim;

        updateCarline({
          oneGraphService,
          locale,
          changedFeature,
          id: carlineIdentifier.current,
        })
          .then(({ data }): void => {
            if (mounted) {
              const mappedCarIdentifier = mapCarIdentifier(
                data.changeConfiguredCarFeature.configuredCar?.id,
              );
              const { assets: newImages } = getImageAssets(
                data.changeConfiguredCarFeature.configuredCar?.media?.images,
              );
              const newIdString = data.changeConfiguredCarFeature.configuredCar?.idString ?? '';
              const newAveString =
                data.changeConfiguredCarFeature.configuredCar?.media?.ave?.renderString ?? '';
              setIdString(newIdString);
              setAveString(newAveString);
              if (newImages.length > 0) {
                // Preload the current visible image first (imageIndex)
                // display as soon as first image is loaded
                // load other images in background
                preloadImage(newImages[imageIndex].url)
                  .then(() => {
                    if (mounted) {
                      setLoading(false);
                      setImages(newImages);
                      carlineIdentifier.current = mappedCarIdentifier;

                      storeCacheItem(
                        { color, rim },
                        {
                          images: newImages,
                          mappedCarIdentifier,
                          idString: newIdString,
                          aveString: newAveString,
                        },
                      );

                      preloadRemainingImages(
                        newImages.map((img) => img.url),
                        imageIndex,
                      );
                    }
                  })
                  .catch((err: unknown) => {
                    logger.error('Failed to preload the image:', err);
                    setLoading(false);
                  });
              } else {
                setLoading(false);
              }
            }
          })
          .catch((err: unknown) => {
            logger.error(err);
            setLoading(false);
          });
      }
    }

    return () => {
      mounted = false;
    };
  }, [rim, color]);

  const contextValue: FAStateContextValue = useMemo(
    () => ({
      imageIndexState,
      colorState,
      rimState,
      carline,
      images,
      loading,
      viewMode,
      setViewMode,
      idString,
      aveString,
    }),
    [imageIndexState, color, rim, images, loading, viewMode, idString, aveString],
  );

  return <FAStateContext.Provider value={contextValue}>{children}</FAStateContext.Provider>;
};

export const useFAStateContext = () => {
  const context = useContext(FAStateContext);
  if (!context) {
    return null;
  }
  return context;
};
